import React, {createRef} from "react"
import {findDOMNode} from "react-dom"

import Breadcrumbs from "../../breadcrumbs/breadcrumbs"
import FinderNote from '../items/note/finder-note'
import FinderProject from '../items/project/finder-project'
import VScroll from "../../scrolling/custom-v-scroll/v-scroll"
import {FinderNoteItemType} from "../items/note/@types"
import {SpaceProto} from "../../../model2/space/space-proto"
import Workspace from "../../../model2/space/workspaces/workspace"

type Props = {
    search          : string
    parentProjectId : ID
    data            : any[]

    isSearching     : boolean
    noSubtasks?     : boolean

    onClickCompany  : func
    onClickProject  : func
    onClickNote     : func
    onTab           : func
}

// const debug = "NoteFinderMain"
const debug = false

export default class NoteFinderMain extends React.Component<Props, any> {

    private scrollRef: any          = createRef()
    private selectedItemRef: any    = createRef()

    private optionsLength: number   = 0

    constructor(props: Props) {
        super(props)

        this.state = {
            currentCursorKey: 0
        }
    }

    componentDidUpdate(prevProps: Props, prevState: any, snapshot: any) {
        debug && console.log(debug, 'componentDidUpdate', this.state.currentCursorKey)

        if (this.scrollRef.current) {
            const node: any = findDOMNode(this.selectedItemRef.current)
            if (node) {
                this.scrollRef.current?.setScrollTop(node.offsetTop + node.clientHeight/2,true,250)
            }
        }
    }

    onClickProject = (id: ID) => {
        this.setState({
            currentCursorKey: 0
        }, () => {
            this.props.onClickProject?.(id)
        })
    }

    onKeyDown = (event: any) => {
        debug && console.log(debug, 'onKeyDown', this.state.currentCursorKey, event)

        let newValue
        switch(event.code){
            case 'Tab':
                event.preventDefault()
                this.props.onTab && this.props.onTab()
                break

            case 'ArrowUp':
                if (_.isNil(this.state.currentCursorKey)) {
                    newValue = 0
                } else {
                    newValue = this.state.currentCursorKey - 1
                    if (newValue < 0) newValue = 0
                }

                this.setState({
                    currentCursorKey: newValue
                })
                break

            case 'ArrowDown':
                if (_.isNil(this.state.currentCursorKey)) {
                    newValue = 0
                } else {
                    newValue = this.state.currentCursorKey + 1
                    if (newValue > this.optionsLength - 1) newValue = this.optionsLength - 1;
                }

                this.setState({
                    currentCursorKey: newValue
                })
                break

            case 'Enter':
                event.preventDefault()
                event.stopPropagation()

                if (this.selectedItemRef.current) this.selectedItemRef.current.click()
                break
        }
    }

    renderItems() {
        if (this.props.isSearching) {
            return <div className="searching-label">Searching notes...</div>
        } else {
            let res: any[] = [], filteredProjects: any[] = [], baseKey = 0
            let projects: any[] | undefined = undefined

            switch (this.props.parentProjectId) {
                case null:
                    if (!this.props.search) {
                        const spaces = _.filter(gd.spaces.spacesWithProjects, (space: SpaceProto) => {
                            return space.enabled
                        })

                        _.map(spaces, (space: SpaceProto, key: number) => {
                            const isSelected = key === this.state.currentCursorKey

                            if (space instanceof Workspace) {
                                res.push(
                                    <FinderProject
                                        ref             = {isSelected ? this.selectedItemRef : null}
                                        key             = {space.id}
                                        isHighlighted   = {isSelected}
                                        project         = {space.project}
                                        onClickProject  = {this.onClickProject}
                                    />
                                )
                            } else {
                                res.push(
                                    <FinderProject
                                        ref             = {isSelected ? this.selectedItemRef : null}
                                        key             = {gd.spaces.projects.id}
                                        isHighlighted   = {isSelected}
                                        project         = {gd.spaces.projects.project}
                                        onClickProject  = {()=>{this.onClickProject('projects')}}
                                    />
                                )
                            }
                        })
                        baseKey = res.length
                    }
                    break

                case 'projects':
                    projects = _.filter(gd.spaces.projects.rootProjects, p => {
                        return !p.isTag && !p.isView && !p.isLink
                    })
                    break

                default:
                    projects = _.filter(gd.session.projects.get(this.props.parentProjectId)?.treeChildren, p => {
                        return !p.isTag && !p.isView && !p.isLink
                    })
            }

            if (!this.props.search && projects) {
                filteredProjects = _.orderBy(projects, p => p.name.toLowerCase())

                _.map(filteredProjects, (p, key) => {
                    const isSelected = key === this.state.currentCursorKey
                    res.push(
                        <FinderProject
                            ref             = {isSelected ? this.selectedItemRef : null}
                            key             = {p.id}
                            isHighlighted   = {isSelected}
                            project         = {p}
                            onClickProject  = {this.onClickProject}
                        />
                    )
                })
                baseKey = filteredProjects.length
            }

            if (this.props.data) {
                const filteredNotes = _.orderBy(
                    _.filter(this.props.data, n => !n.projectId || n.projectId === this.props.parentProjectId),
                    n => n.name.toLowerCase()
                )
                _.map(filteredNotes, (n: FinderNoteItemType, key: number) => {
                    const itemKey = baseKey + key
                    const isSelected = itemKey === this.state.currentCursorKey
                    res.push(
                        <FinderNote
                            ref             = {isSelected ? this.selectedItemRef : null}
                            key             = {n.id}
                            isHighlighted   = {isSelected}
                            noteItem        = {n}
                            onClickNote     = {this.props.onClickNote}
                        />
                    )
                })
            }

            const notesLength = this.props.data ? this.props.data.length : 0
            if (!this.props.search) {
                this.optionsLength = filteredProjects.length + notesLength
            } else {
                this.optionsLength = notesLength
            }

            return <div className="search-items">{res}</div>
        }
    }

    renderNotification() {
        const projects      = _.filter(gd.session.projects.treeRoots, p=>p.parentId === this.props.parentProjectId);
        const showNoNotes   = projects.length <= 0 && !!this.props.parentProjectId && !!this.props.data && this.props.data.length === 0 && !this.props.search;
        const noMatches     = !!this.props.data && this.props.data.length === 0 && !!this.props.search && !this.props.isSearching;
        const showLoading   = !this.props.data && !this.props.search && !this.props.isSearching;

        let label = null;
        if (showNoNotes) label = <div className="not-found">No notes found</div>;
        else if (noMatches) label = <div className="not-found">No notes found</div>;
        else if (showLoading) label = <div className="not-found">Loading...</div>;

        return label;
    }

    render() {
        const showBreadcrumbs = !this.props.search && this.props.parentProjectId

        const mainClasses = Classes("finder-main-container", {
            'with-breadcrumbs': showBreadcrumbs
        })

        return (
            <div ref="main" className="finder-main">
                {showBreadcrumbs &&
                    <div className="flex-column-left-top m-l-15 m-r-30">
                        <Breadcrumbs
                            className                       = "finder-breadcrumbs"
                            companyId                       = {gd.companyId}
                            projectId                       = {null}
                            currentProject                  = {this.props.parentProjectId}
                            currentProjectAsLastBreadcrumb  = {true}
                            onNavigateCompany               = {this.props.onClickCompany}
                            onNavigateProject               = {this.onClickProject}
                        />
                        <div className="horizontal-separator"/>
                    </div>
                }

                <VScroll
                    ref             = {this.scrollRef}
                    containerClass  = {mainClasses}
                    minDragHeight   = {20}
                >
                    {this.renderItems()}
                    {this.renderNotification()}
                </VScroll>
            </div>
        )
    }
}